import { GeoJsonLayer } from '@deck.gl/layers';
import type { Layer, LayersList, Unit } from '@deck.gl/core';
import type { LayerSource, RasterLayerSource } from '@/hooks/useDatasets';
import { ID_TOKEN_SESSION_KEY, USER_EMAIL } from '@/app/constants';
import { getSatelliteBaseLayer } from './baseLayer';
import { getEncroachmentLayer, OVERHANG_LAYER_TYPE } from './encroachment';
import { getInspectLayer } from './inspectLayer';
import { getLineLayer } from './lines';
import { getPoleLayer } from './poles';
import { getSegmentationLayer } from './segmentation';
import { getSpeciesLayer } from './species';
import {
  getTreesAggregateDataTileLayer,
  getTreeTypeDataTileLayer,
} from './trees';
import {
  getTreesInDeclineLayer,
  TREE_POINTS_ZOOM,
  TREE_TYPES,
} from './treesInDecline';
import { getTreeTypeLayer } from './treeType';
import type {
  EncroachmentLayerConfig,
  LayerConfig,
  LayerConfigMap,
  RiskTypeConfig,
  TreesAggregateData,
} from './types';
import { LayerType } from './types';
import type { URLTemplate } from '@/views/map/types';
import { getWildfireRiskLayer } from './wildfireRisk';
import { getFuelHazardLayer } from './fuelHazard';
import { getSatelliteBaseMetadataLayer } from './satelliteMetadataLayer';
import { getSatelliteBaseSubLayers } from './satelliteMetadataSubLayer';
import {
  GroupAnalysisType,
  type GroupSummary,
} from '@/generated/types/navigator/types.gen';
import { getCriticalTreesLayer } from './criticalTrees';
import { addMvtCachingParams, getHeaders } from '@/features/utils';
import { getNetworkGroupLayer } from './networkGroupLayer';
import {
  getNetworkOutlineLayer,
  shouldShowNetworkOutline,
} from './networkOutline';
import { getWorktypeAnalysisLayer } from './worktypeAnalysis/worktypeAnalysis';
import type { SelectedItem } from '@/features/analysisMapSlice';
import type { FilterState } from '@/features/filterSlice';
import type { Feature } from 'geojson';
import type { HoverInfo } from '@/views/MapView';
import type { Dispatch, SetStateAction } from 'react';
import { type Species } from '@/features/fieldSlice';
import { getRedFlagWarningLayer } from './redFlagWarning';
import { type RedFlagWarning } from '@/features/wildfireSlice';
import { isTreeCoverActive } from '@/views/map/utils';
import { isFilterEmpty } from '@/features/filterHelpers';
import type { GroupId } from '@/features/types';

type LayerFn = (
  reference: string,
  config: LayerConfig,
  apiIdentity?: string
) => Layer;

// Raster tile layers gated by a user-facing visibility toggle. Once one has
// been visible at least once this session we keep it in the deck list so its
// TileLayer instance (and cached tiles) survive subsequent toggle-off/on
// cycles. Reset on full page reload.
//
// Layers NOT in this set:
// - base and distance_bucket have their own always-on / tree-cover-gated
//   rules further down (distance_bucket only feeds inspect hover).
// - MVT layers (trees, poles, lines, scan, tree_type) hit navigator, not
//   tiler, and have their own gating (analysis mode, zoom threshold, etc.).
// - Presentation-only helpers (highlight, red flag warning) don't fetch
//   tiles at all.
const STICKY_LAYER_TYPES = [
  LayerType.fuel_hazard,
  LayerType.wildfire_risk,
  LayerType.worktype_analysis,
  LayerType.segmentation,
  LayerType.encroachment,
] as const;
const activatedLayers = new Set<LayerType>();

export const getLayers = (
  datasets: LayerSource[],
  isOverview: boolean,
  apiIdentity: string,
  query: FilterState,
  listId: string | null,
  layerConfig: LayerConfigMap,
  isInspectMode: boolean,
  statusChanged: boolean,
  selectedNetworkGroupId: GroupId | null,
  mapNetworkGroupId: GroupId | null,
  updatedSpecies: Species[],
  setHoverInfo: Dispatch<SetStateAction<HoverInfo>>,
  selectedAnalysis: GroupAnalysisType,
  treesData: Feature[],
  setTreesData: Dispatch<SetStateAction<Feature[]>>,
  treesAggregateData: TreesAggregateData[],
  setTreesAggregateData: Dispatch<SetStateAction<TreesAggregateData[]>>,
  setSatelliteMetadata: Dispatch<SetStateAction<Feature[]>>,
  satelliteMetadata: Feature[],
  isSmallScreen: boolean,
  showHazardTrees: boolean,
  showDecliningTrees: boolean,
  showStrikeTrees: boolean,
  zoom: number,
  encroachmentThresholds: number[],
  priorityThresholds: number[],
  wildfirePriorityThresholds: number[],
  criticalTreesThresholds: number[],
  clearThresholds: number[],
  spanNetworkGroupId: GroupId | undefined,
  activeGroupName: string | null,
  groupSummary: Map<string, GroupSummary>,
  redFlagWarnings: RedFlagWarning[] | undefined,
  mvtCacheCounter: number,
  hoveredGroupName: string | undefined,
  fuelHazardMaxScore: number | null | undefined,
  hasGroupRiskSummary = false,
  hasFuelsWithoutWildfirePriority = false,
  usePostForMvtGroupAnalysis = false,
  networkOutlineEnabled = false,
  scopeQuery?: string,
  planId?: string
) => {
  const deckLayers: LayersList = [];

  const addDeckMVTLayer = (type: LayerType, getLayerFn: LayerFn) =>
    addLayer(type, getLayerFn, datasets, layerConfig, deckLayers, apiIdentity);

  for (const type of STICKY_LAYER_TYPES) {
    if (layerConfig[type]?.visible) activatedLayers.add(type);
  }
  // distance_bucket only feeds the inspect tooltip, which only shows when tree
  // cover is on. Sticky-activate it alongside any tree-cover sub-layer so its
  // tile cache is preserved across toggle cycles.
  if (isTreeCoverActive(layerConfig)) {
    activatedLayers.add(LayerType.distance_bucket);
  }

  const base = getRasterSourceForType(LayerType.base, datasets);
  if (base) {
    deckLayers.push(getSatelliteBaseLayer(base, layerConfig[LayerType.base]));
  }

  const wildfireRisk = getRasterSourceForType(
    LayerType.wildfire_risk,
    datasets
  );
  if (wildfireRisk && activatedLayers.has(LayerType.wildfire_risk)) {
    deckLayers.push(
      getWildfireRiskLayer(
        wildfireRisk,
        layerConfig[LayerType.wildfire_risk],
        isOverview
      )
    );
  }

  const fuelHazard = getRasterSourceForType(LayerType.fuel_hazard, datasets);
  if (fuelHazard && activatedLayers.has(LayerType.fuel_hazard)) {
    deckLayers.push(
      getFuelHazardLayer(
        fuelHazard,
        layerConfig[LayerType.fuel_hazard],
        isOverview,
        fuelHazardMaxScore
      )
    );
  }

  const worktypeAnalysis = getRasterSourceForType(
    LayerType.worktype_analysis,
    datasets
  );
  if (worktypeAnalysis && activatedLayers.has(LayerType.worktype_analysis)) {
    deckLayers.push(
      getWorktypeAnalysisLayer(
        worktypeAnalysis,
        layerConfig[LayerType.worktype_analysis],
        isOverview
      )
    );
  }

  const segmentation = getRasterSourceForType(LayerType.segmentation, datasets);
  if (segmentation && activatedLayers.has(LayerType.segmentation)) {
    deckLayers.push(
      getSegmentationLayer(segmentation, layerConfig[LayerType.segmentation])
    );
  }

  const distanceBucket = getRasterSourceForType(
    LayerType.distance_bucket,
    datasets
  );

  if (distanceBucket && activatedLayers.has(LayerType.distance_bucket)) {
    deckLayers.push(
      getInspectLayer(distanceBucket, layerConfig[LayerType.distance_bucket])
    );
  }

  const encroachment = getRasterSourceForType(LayerType.encroachment, datasets);
  if (encroachment && activatedLayers.has(LayerType.encroachment)) {
    const overhang = getRasterSourceForType(OVERHANG_LAYER_TYPE, datasets);
    deckLayers.push(
      getEncroachmentLayer(
        encroachment,
        layerConfig[LayerType.encroachment] as EncroachmentLayerConfig,
        overhang,
        isInspectMode
      )
    );
  }

  const treeType = getRasterSourceForType(LayerType.tree_type, datasets);
  if (treeType) {
    deckLayers.push(
      getTreeTypeLayer(
        treeType,
        layerConfig[LayerType.tree_type],
        isInspectMode
      )
    );
  }

  const species = getDatasetForType(LayerType.tree_species, datasets);
  if (species) {
    deckLayers.push(
      getSpeciesLayer(
        layerConfig[LayerType.tree_species],
        updatedSpecies,
        setHoverInfo,
        isInspectMode,
        apiIdentity
      )
    );
  }

  const satelliteBaseMetadata = getDatasetForType(
    LayerType.satellite_metadata,
    datasets
  );
  if (satelliteBaseMetadata) {
    deckLayers.push(
      getSatelliteBaseMetadataLayer(apiIdentity, setSatelliteMetadata)
    );

    // These layers need to be separate from satelliteBaseMetadataLayer since we
    // need to collect all results from MVTLayer requests before showing single
    // GeoJsonLayer(s) for the viewport
    if (satelliteMetadata) {
      deckLayers.push(getSatelliteBaseSubLayers(satelliteMetadata));
    }
  }

  if (
    wildfireRisk &&
    redFlagWarnings != undefined &&
    redFlagWarnings?.length > 0
  ) {
    deckLayers.push(
      getRedFlagWarningLayer(
        redFlagWarnings,
        layerConfig[LayerType.red_flag_warning]
      )
    );
  }

  const scan = getDatasetForType(LayerType.scan, datasets);

  // Pushed before the scan layer so it draws underneath: the coloured network covers the
  // spans the filter keeps, leaving the dashed outline showing through for the rest.
  // Drilling into a group counts as filtering — that is when the surrounding network
  // disappears and the outline is most worth having.
  if (
    scan &&
    shouldShowNetworkOutline({
      flagEnabled: networkOutlineEnabled,
      zoom,
      isFilterActive:
        scopeQuery !== undefined ||
        planId !== undefined ||
        !isFilterEmpty(query) ||
        Boolean(listId) ||
        Boolean(activeGroupName),
    })
  ) {
    deckLayers.push(getNetworkOutlineLayer(apiIdentity));
  }

  if (scan) {
    deckLayers.push(
      getNetworkGroupLayer(
        isOverview,
        layerConfig[LayerType.scan],
        apiIdentity,
        query,
        selectedAnalysis,
        listId,
        selectedNetworkGroupId,
        encroachmentThresholds,
        priorityThresholds,
        wildfirePriorityThresholds,
        criticalTreesThresholds,
        clearThresholds,
        spanNetworkGroupId,
        activeGroupName,
        zoom,
        groupSummary,
        statusChanged,
        mapNetworkGroupId,
        mvtCacheCounter,
        hoveredGroupName,
        hasGroupRiskSummary,
        hasFuelsWithoutWildfirePriority,
        usePostForMvtGroupAnalysis,
        layerConfig[LayerType.work_status].visible,
        scopeQuery,
        planId
      )
    );
  }

  const trees = getDatasetForType(LayerType.trees, datasets);
  if (trees) {
    let fetchHazardTrees = showHazardTrees;
    if (
      selectedAnalysis === GroupAnalysisType.FALL_IN &&
      zoom < TREE_POINTS_ZOOM
    ) {
      fetchHazardTrees = false;
    }

    if (fetchHazardTrees) {
      deckLayers.push(
        getTreeTypeDataTileLayer(
          'hazard',
          apiIdentity,
          query,
          listId,
          setTreesData,
          selectedNetworkGroupId,
          activeGroupName
        )
      );
    }

    if (showStrikeTrees && zoom >= TREE_POINTS_ZOOM) {
      deckLayers.push(
        getTreeTypeDataTileLayer(
          'strike',
          apiIdentity,
          query,
          listId,
          setTreesData,
          selectedNetworkGroupId,
          activeGroupName
        )
      );
    }

    if (showDecliningTrees) {
      deckLayers.push(
        getTreeTypeDataTileLayer(
          'declining',
          apiIdentity,
          query,
          listId,
          setTreesData,
          selectedNetworkGroupId,
          activeGroupName
        )
      );
    }

    if (selectedAnalysis === GroupAnalysisType.FALL_IN) {
      deckLayers.push(
        getTreesAggregateDataTileLayer(
          setTreesAggregateData,
          apiIdentity,
          query,
          listId,
          selectedNetworkGroupId,
          activeGroupName
        )
      );
    }
  }

  if (treesData && treesAggregateData) {
    deckLayers.push(
      getTreesInDeclineLayer(
        layerConfig[LayerType.trees] as RiskTypeConfig,
        treesData,
        treesAggregateData,
        isSmallScreen
      )
    );
  }

  if (getDatasetForType(LayerType.critical_trees, datasets)) {
    // Only show critical trees layer when on workload analysis
    if (selectedAnalysis === GroupAnalysisType.WORKLOAD_ANALYSIS) {
      addDeckMVTLayer(LayerType.critical_trees, getCriticalTreesLayer);
    }
  }

  deckLayers.push(
    getLineLayer(
      layerConfig[LayerType.lines],
      apiIdentity,
      zoom,
      selectedNetworkGroupId,
      activeGroupName,
      spanNetworkGroupId,
      mapNetworkGroupId
    )
  );
  addDeckMVTLayer(LayerType.poles, getPoleLayer);

  return deckLayers;
};

export const getAllHeaders = (headers = {}, apiIdentity?: string) => {
  const token = sessionStorage.getItem(ID_TOKEN_SESSION_KEY) ?? '';
  if (!apiIdentity) {
    apiIdentity = sessionStorage.getItem(USER_EMAIL) ?? '';
  }

  return getHeaders(token, apiIdentity, headers);
};

const addLayer = (
  type: LayerType,
  getLayerFn: LayerFn,
  datasets: LayerSource[],
  layerConfig: Record<LayerType, LayerConfig>,
  layers: LayersList,
  apiIdentity: string
) => {
  const source = getDatasetForType(type, datasets);
  if (source) {
    layers.push(getLayerFn(source.layerType, layerConfig[type], apiIdentity));
  }
};

const getDatasetForType = (
  type: LayerType | string,
  datasets: LayerSource[]
) => {
  const layerType = typeof type === 'string' ? type : LayerType[type];
  const filtered = datasets.filter((d) => d.layerType === layerType);
  if (filtered.length === 1) {
    return filtered[0];
  }
  return null;
};

export const getRasterSourceForType = (
  type: LayerType | string,
  datasets: LayerSource[]
): RasterLayerSource | null => {
  const source = getDatasetForType(type, datasets);
  return source?.kind === 'raster' ? source : null;
};

export const getDefaultConfig = (datasetId: string, apiIdentity: string) => {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const data: URLTemplate = `/v1/mvt/${datasetId}/{z}/{x}/{y}`;
  return {
    data: addMvtCachingParams({}, data),
    loadOptions: {
      fetch: {
        headers: getAllHeaders({}, apiIdentity),
        credentials: 'include',
      },
    },
    binary: true, // Performance boost by avoiding serde
    pickable: false,
    _typedArrayManagerProps: isMobile ? { overAlloc: 1, poolSize: 0 } : null,
    autoHighlight: true,
    highlightColor: [0, 0, 0, 50],
    lineWidthUnits: 'pixels' as Unit,
    parameters: {
      depthTest: false,
    },
    maxRequests: 3, // Adding this to reduce the requests to be called concurrently, default is 6
  };
};

export const getHighlightLayer = (
  selectedItemObj: SelectedItem['object'],
  isMobileInspectMode?: boolean
) => {
  const treeType = selectedItemObj?.properties?.type;
  const config =
    treeType !== undefined && TREE_TYPES.includes(treeType)
      ? {
          // We currently only highlight strike tree crowns
          data: treeType === 'strike' ? [selectedItemObj] : [],
          getLineWidth: 0.2,
          getFillColor: [235, 255, 251, 90],
          getLineColor: [175, 236, 223],
        }
      : {
          data: selectedItemObj,
          lineWidthScale: 15,
          getLineWidth: 5,
          getFillColor: [255, 255, 255, 20],
          getLineColor: [49, 226, 188, 80],
        };

  return new GeoJsonLayer({
    ...config,
    id: 'highlight_layer',
    visible: !isMobileInspectMode,
    pickable: false,
    filled: true,
    getPointRadius: 3,
    pointRadiusMaxPixels: 8,
    lineWidthMaxPixels: 10,
  } as Record<string, unknown>);
};
